"use client";

import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from "react";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import ProfessionalMatchBanner from "@/app/(fevicol-inhomz)/_components/global/ProfessionalMatchBanner";
import ContractorCard from "../../../_components/global/ContractorCard";
import Skeleton from "../../../_components/global/Skeleton";
import { useContractorsQuery } from "../_provider/ContractorsQueryProvider";
import { useInfiniteScroll } from "../_hooks/useInfiniteScroll";
import { mapContractorProfile } from "../_utils/mapContractorProfile";
import { useSurveySheetStore } from "@/store/surveySheetStore";
import { getSurveyAnswers, isSurveyCompleted } from "@/lib/utils/survey-storage";
import { inhomzEvents } from "../../../_lib/events";
import { useInhomzTrack } from "../../../_hooks/useInhomzTrack";

// Break up a long results list with a match-banner CTA: after the 6th
// contractor once the list is long enough for that to matter, otherwise
// after the 2nd (a fixed cut, not a proportional half — a 3-contractor list
// gets 2 before/1 after, not 2/1 by coincidence of rounding).
function splitAroundBanner(contractors) {
  const bannerPosition = contractors.length > 6 ? 6 : 2;
  return [contractors.slice(0, bannerPosition), contractors.slice(bannerPosition)];
}

// The page (see page.jsx) already renders this inside a MaxWidth column next
// to FiltersSidebar, so batches just fill that column — no MaxWidth of their
// own, and the match banner between batches is bounded by it too rather than
// bleeding full page width. Grid is 1 column on mobile, 2 from tablet (md) up
// — a 3rd column doesn't fit once the desktop sidebar is in play.
function ContractorCardBatch(t0) {
  const $ = _c(7);
  const {
    contractors,
    startNo: t1
  } = t0;
  const startNo = t1 === undefined ? 1 : t1;
  let t2;
  if ($[0] !== contractors || $[1] !== startNo) {
    let t3;
    if ($[3] !== startNo) {
      t3 = (contractor, index) => <li key={contractor.contractorUuid}><ContractorCard {...mapContractorProfile(contractor)} trackingEvent={inhomzEvents.contractors.CONTRACTOR_CARD_CLICKED} cardNo={startNo + index} /></li>;
      $[3] = startNo;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = contractors.map(t3);
    $[0] = contractors;
    $[1] = startNo;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">{t2}</ul>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function ContractorCardSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="relative aspect-[343/160] w-full flex-shrink-0"><Skeleton width={null} height={null} rounded="rounded-none" className="h-full w-full" /><Skeleton width={64} height={64} rounded="rounded-full" className="absolute -bottom-2 left-4 border-[3px] border-[--colorSurfaceSurface]" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col overflow-hidden rounded-[--radius8] border border-[--colorDisabledOnColorDisabled] bg-[--colorSurfaceSurface]">{t0}<div className="flex flex-col gap-2 px-4 pb-3 pt-4"><Skeleton height={16} width="60%" /><Skeleton height={12} width="40%" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ContractorCardSkeletonGrid(t0) {
  const $ = _c(2);
  const {
    count
  } = t0;
  let t1;
  if ($[0] !== count) {
    t1 = <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">{Array.from({
        length: count
      }).map(_temp)}</ul>;
    $[0] = count;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, index) {
  return <li key={index}><ContractorCardSkeleton /></li>;
}
export default function ContractorsList() {
  const $ = _c(22);
  const {
    contractors,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useContractorsQuery();
  const loadMoreRef = useInfiniteScroll(fetchNextPage, hasNextPage, isFetchingNextPage, 0.1);
  const openSurvey = useSurveySheetStore(_temp2);
  const track = useInhomzTrack();
  const isSurveyDone = useSyncExternalStore(useSurveySheetStore.subscribe, _temp3, _temp4);
  if (isLoading && contractors.length === 0) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <div className="p-1"><ContractorCardSkeletonGrid count={6} /></div>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (error && contractors.length === 0) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <p className="m3-body-medium p-4 text-center text-[--textSecondarySecondary]">Something went wrong loading professionals. Please try again.</p>;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (contractors.length === 0) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <p className="m3-body-medium p-4 text-center text-[--textSecondarySecondary]">No professionals match your filters yet. Try clearing a few.</p>;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== contractors) {
    t0 = splitAroundBanner(contractors);
    $[3] = contractors;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const [contractorsBeforeBanner, contractorsAfterBanner] = t0;
  let t1;
  if ($[5] !== contractorsBeforeBanner) {
    t1 = <ContractorCardBatch contractors={contractorsBeforeBanner} />;
    $[5] = contractorsBeforeBanner;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== contractorsAfterBanner || $[8] !== contractorsBeforeBanner || $[9] !== isSurveyDone || $[10] !== openSurvey || $[11] !== track) {
    t2 = contractorsAfterBanner.length > 0 && <>{!isSurveyDone && <ProfessionalMatchBanner className="rounded-lg" buttonClassName="self-center md:self-end" icon={<AssignmentOutlinedIcon sx={{
        fontSize: 60,
        color: "var(--colorWarningWarningContainer)"
      }} />} title="Tell Us About Your Project" description="Answer a few quick questions and we'll show you Interior Experts for your kind of project." buttonLabel="Get started" buttonIcon={<ModeEditOutlinedIcon />} onButtonClick={() => {
        track(inhomzEvents.contractors.SURVEY_CLICKED, {
          placement: "banner"
        });
        openSurvey();
      }} />}<ContractorCardBatch contractors={contractorsAfterBanner} startNo={contractorsBeforeBanner.length + 1} /></>;
    $[7] = contractorsAfterBanner;
    $[8] = contractorsBeforeBanner;
    $[9] = isSurveyDone;
    $[10] = openSurvey;
    $[11] = track;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== isFetchingNextPage) {
    t3 = isFetchingNextPage && <ContractorCardSkeletonGrid count={2} />;
    $[13] = isFetchingNextPage;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== loadMoreRef || $[16] !== t3) {
    t4 = <div ref={loadMoreRef} className="min-h-[1px]">{t3}</div>;
    $[15] = loadMoreRef;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t4) {
    t5 = <div className="flex flex-col gap-3 p-1">{t1}{t2}{t4}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
}
function _temp4() {
  return false;
}
function _temp3() {
  return isSurveyCompleted(getSurveyAnswers());
}
function _temp2(state) {
  return state.open;
}